export const training = {
  title: 'Treinamento Comportamental',
  subtitle: 'Aprenda a ler e interpretar perfis comportamentais',
  
  modules: {
    foundations: {
      title: 'Fundamentos',
      description: 'Aprenda o básico de leitura de perfis'
    },
    framework: {
      title: 'A Estrutura',
      description: 'Mergulhe profundamente no modelo comportamental de cinco dimensões'
    },
    application: {
      title: 'Aplicação',
      description: 'Coloque seu conhecimento em prática em cenários reais'
    }
  },
  
  responsibleUse: {
    title: 'Uso Responsável',
    points: [
      'Perfis são um ponto de dados—combine com entrevistas e experiência',
      'Pontuações mostram preferências, não capacidades ou limitações',
      'O contexto importa—o mesmo perfil pode se destacar ou ter dificuldades dependendo do cargo',
      'Nunca use perfis para tomar decisões de contratação de aprovação/reprovação',
      'Perfis não devem ser usados para excluir candidatos de grupos protegidos',
      'Os resultados individuais podem variar—sempre entreviste candidatos independentemente da correspondência do perfil'
    ]
  },
  
  legalDisclaimer: {
    title: 'Aviso Legal e Declaração de Não Discriminação',
    points: [
      'Esta avaliação não é uma avaliação psicológica, triagem médica ou ferramenta de diagnóstico de deficiência',
      'Perfis comportamentais nunca devem ser a única base para decisões de emprego',
      'Não use perfis para excluir candidatos com base em características que possam se correlacionar com classes protegidas (raça, cor, religião, sexo, origem nacional, idade, deficiência, informações genéticas)',
      'As organizações devem cumprir as leis trabalhistas aplicáveis, incluindo legislação antidiscriminação',
      'Acomodações razoáveis podem afetar como os indivíduos respondem às avaliações—consulte um advogado',
      'Exemplos de correspondência de funções são apenas ilustrativos—determinações reais de adequação ao trabalho requerem análise formal de trabalho validada para suas funções específicas',
      'Os empregadores são os únicos responsáveis por garantir que seu uso desta ferramenta esteja em conformidade com todas as leis trabalhistas federais, estaduais e locais aplicáveis'
    ]
  },
  
  sections: {
    chart: {
      title: 'Compreendendo o Gráfico',
      subtitle: 'Como ler visualizações comportamentais',
    },
    percentile: {
      title: 'A Escala de Percentil',
      subtitle: 'O que as pontuações significam em contexto',
    },
    traits: {
      title: 'Os Cinco Traços Comportamentais',
      subtitle: 'Compreendendo as dimensões D-C-T-O-S',
    },
    width: {
      title: 'Largura do Perfil: Amplo vs Estreito',
      subtitle: 'O que a distribuição das pontuações indica',
    },
    cognitive: {
      title: 'Avaliação Cognitiva',
      subtitle: 'Habilidade mental e resolução de problemas',
    },
    reasoning: {
      title: 'Estilos de Raciocínio',
      subtitle: 'Como as pessoas abordam a resolução de problemas',
    },
    executionSpeed: {
      title: 'Velocidade de Execução: O Gap D-T',
      subtitle: 'Como a relação Drive-Task afeta o ritmo',
    },
    applications: {
      title: 'Aplicações Práticas',
      subtitle: 'Como usar perfis no mundo real',
    },
    talentDesign: {
      title: 'Design de Talentos: Construindo o Time Certo',
      subtitle: 'A estrutura Gás-Cola-Freio para composição de equipes',
    }
  },

  chartExplainer: {
    title: 'Compreendendo o Gráfico',
    subtitle: 'Cada perfil comportamental é exibido como um gráfico visual. Veja como lê-lo:',
    traits: {
      dominance: 'Drive',
      extraversion: 'Connection',
      patience: 'Task',
      formality: 'Order',
      objectivity: 'Style'
    },
    colorLegend: {
      title: 'Traços Codificados por Cor'
    },
    position: {
      title: 'Significado da Posição do Ponto',
      left: {
        label: 'Lado Esquerdo (0-40%)',
        description: 'Menor expressão do traço'
      },
      center: {
        label: 'Centro (40-60%)',
        description: 'Expressão moderada e equilibrada'
      },
      right: {
        label: 'Lado Direito (60-100%)',
        description: 'Maior expressão do traço'
      }
    },
    howToRead: {
      title: 'Como ler:',
      description: 'Passe o mouse sobre qualquer ponto para ver a pontuação percentual exata. A distribuição visual mostra rapidamente se alguém tem um perfil amplo (especializado) ou estreito (versátil).'
    }
  },

  percentileScale: {
    title: 'A Escala de Percentil',
    subtitle: 'Todas as pontuações são mostradas como percentis (0-100%) comparando alguém à população geral.',
    fiftyPercent: {
      title: 'O que Significa 50%?',
      description: '50% é a média da população. A maioria das pessoas pontua entre 40-60% em cada traço. As pontuações seguem uma distribuição em curva de sino.'
    },
    understanding: {
      title: 'Compreendendo Faixas de Pontuação'
    },
    ranges: {
      low: {
        label: 'Pontuação Baixa',
        description: 'Traço é menos proeminente - reflete a tendência comportamental oposta'
      },
      moderate: {
        label: 'Pontuação Moderada',
        description: 'Expressão equilibrada - pode se adaptar entre ambos os extremos'
      },
      high: {
        label: 'Pontuação Alta',
        description: 'Traço é proeminente - forte preferência por este estilo comportamental'
      }
    },
    analogy: {
      title: 'Analogia do Mundo Real',
      description: 'Pense em percentis como altura: 50% significa altura média, 80% significa mais alto que 80% das pessoas, 20% significa mais alto que apenas 20% das pessoas. O mesmo princípio se aplica aos traços comportamentais.'
    }
  },

  cognitive: {
    title: 'Visão Geral da Avaliação Cognitiva',
    subtitle: 'O teste cognitivo mede capacidade mental e habilidade de resolução de problemas.',
    whatItMeasures: {
      title: 'O que Mede',
      description: 'A avaliação cognitiva avalia três habilidades mentais principais:',
      items: {
        verbal: 'Raciocínio verbal - compreender e analisar informações escritas',
        numerical: 'Raciocínio numérico - trabalhar com números e dados quantitativos',
        abstract: 'Raciocínio abstrato - reconhecer padrões e resolver problemas novos'
      }
    },
    difference: {
      title: 'Como Difere do Comportamental',
      description: 'Traços comportamentais medem COMO você prefere trabalhar (seu estilo). Capacidade cognitiva mede O QUÃO BEM você resolve problemas complexos (sua capacidade).',
      highlight: 'Diferença chave: Traços comportamentais mostram preferências e tendências. Pontuações cognitivas mostram capacidade e velocidade de aprendizado.'
    },
    usingBoth: {
      title: 'Usando Ambos Juntos para Contratação',
      description: 'As decisões de contratação mais eficazes combinam ambas as avaliações:',
      items: {
        behavioral: 'Perfil Comportamental → Eles vão gostar do papel e se encaixar na cultura?',
        cognitive: 'Pontuação Cognitiva → Eles conseguem lidar com a complexidade e aprender rapidamente?',
        combined: 'Juntos → Imagem completa de adequação, capacidade e potencial'
      }
    },
    format: {
      title: 'Formato do Teste',
      description: '50 questões, 12 minutos, cobrindo raciocínio verbal, numérico e abstrato. Resultados mostram percentil geral e detalhamento por tipo de raciocínio.'
    }
  },

  traits: {
    dominance: {
      letter: 'D',
      name: 'Drive',
      definition: 'Mede o quão direto e assertivo alguém é na busca de objetivos e resultados.',
      high: {
        title: 'Pontuação Alta (60%+)',
        characteristics: 'Direto, decisivo, orientado a resultados, assume a liderança, confortável com conflito',
        strengths: 'Realiza tarefas rapidamente, toma decisões difíceis, impulsiona mudanças',
        watchouts: 'Pode parecer agressivo, pode ignorar sentimentos, pode apressar decisões'
      },
      low: {
        title: 'Pontuação Baixa (40%-)',
        characteristics: 'Colaborativo, apoiador, busca consenso, evita conflitos, paciente',
        strengths: 'Constrói relacionamentos fortes, ouve bem, cria harmonia',
        watchouts: 'Pode evitar confrontos necessários, pode ser indeciso, pode ceder demais'
      },
      moderate: {
        title: 'Pontuação Moderada (40-60%)',
        characteristics: 'Abordagem equilibrada - assertivo quando necessário, colaborativo quando apropriado'
      }
    },
    extraversion: {
      letter: 'C',
      name: 'Connection',
      definition: 'Mede o quão expansivo e social alguém é, e como ganha energia.',
      high: {
        title: 'Pontuação Alta (60%+)',
        characteristics: 'Expansivo, entusiasta, sociável, energizado por pessoas, expressivo',
        strengths: 'Constrói redes facilmente, energiza equipes, comunicador persuasivo',
        watchouts: 'Pode dominar conversas, pode parecer superficial, pode ignorar introvertidos'
      },
      low: {
        title: 'Pontuação Baixa (40%-)',
        characteristics: 'Reservado, reflexivo, focado, prefere conexões profundas, observador',
        strengths: 'Pensa antes de falar, ouve profundamente, trabalha bem independentemente',
        watchouts: 'Pode parecer distante, pode ser negligenciado, pode perder oportunidades de networking'
      },
      moderate: {
        title: 'Pontuação Moderada (40-60%)',
        characteristics: 'Ambivertido - confortável tanto socialmente quanto independentemente'
      }
    },
    patience: {
      letter: 'T',
      name: 'Task',
      definition: 'Mede preferência por estabilidade e consistência versus mudança e variedade.',
      high: {
        title: 'Pontuação Alta (60%+)',
        characteristics: 'Estável, consistente, confiável, prefere estabilidade, paciente, metódico',
        strengths: 'Mantém padrões de qualidade, constrói sistemas duradouros, presença tranquilizadora',
        watchouts: 'Pode resistir a mudanças necessárias, pode ser lento para se adaptar, pode evitar inovação'
      },
      low: {
        title: 'Pontuação Baixa (40%-)',
        characteristics: 'Dinâmico, rápido, prospera com mudança, busca variedade, inquieto',
        strengths: 'Adapta-se rapidamente, impulsiona inovação, multitarefa efetivamente',
        watchouts: 'Pode parecer impaciente, pode perturbar estabilidade, pode perder detalhes importantes'
      },
      moderate: {
        title: 'Pontuação Moderada (40-60%)',
        characteristics: 'Equilibrado - aprecia tanto estabilidade quanto mudança apropriada'
      }
    },
    formality: {
      letter: 'O',
      name: 'Order',
      definition: 'Mede preferência por estrutura e detalhes versus flexibilidade e visão geral.',
      high: {
        title: 'Pontuação Alta (60%+)',
        characteristics: 'Estruturado, orientado a detalhes, segue procedimentos, analítico, cauteloso',
        strengths: 'Garante precisão, mantém conformidade, detecta erros',
        watchouts: 'Pode parecer rígido, pode se perder em detalhes, pode retardar progresso'
      },
      low: {
        title: 'Pontuação Baixa (40%-)',
        characteristics: 'Flexível, inovador, foco em visão geral, espontâneo, assume riscos',
        strengths: 'Pensa criativamente, adapta-se facilmente, identifica oportunidades',
        watchouts: 'Pode negligenciar detalhes, pode parecer descuidado, pode ignorar procedimentos importantes'
      },
      moderate: {
        title: 'Pontuação Moderada (40-60%)',
        characteristics: 'Equilibrado - estruturado quando necessário, flexível quando apropriado'
      }
    },
    objectivity: {
      letter: 'S',
      name: 'Style',
      definition: 'Mede estilo de pensamento - analítico e orientado a dados versus intuitivo e focado em pessoas.',
      high: {
        title: 'Pontuação Alta (60%+)',
        characteristics: 'Analítico, orientado a dados, lógico, objetivo, focado em fatos',
        strengths: 'Toma decisões racionais, resolve problemas complexos, traz objetividade',
        watchouts: 'Pode negligenciar emoções, pode parecer frio, pode perder fatores humanos'
      },
      low: {
        title: 'Pontuação Baixa (40%-)',
        characteristics: 'Intuitivo, focado em pessoas, empático, orientado a relacionamentos, orientado por valores',
        strengths: 'Lê pessoas bem, constrói confiança, considera impacto nos outros',
        watchouts: 'Pode ser influenciado por emoções, pode negligenciar dados, pode evitar decisões difíceis'
      },
      moderate: {
        title: 'Pontuação Moderada (40-60%)',
        characteristics: 'Equilibrado - usa tanto lógica quanto intuição apropriadamente'
      }
    }
  },

  width: {
    wide: {
      title: 'Perfil Amplo',
      definition: 'Grande diferença entre pontuações mais altas e mais baixas (ex: 85%-25%)',
      characteristics: [
        'Tendências comportamentais mais especializadas',
        'Forças claras e pronunciadas em certas áreas',
        'Pode precisar de suporte ou desenvolvimento em outras áreas',
        'Indicadores fortes de adequação a funções',
        'Assinatura de personalidade mais definida'
      ],
      example: 'D=85%, C=75%, T=25%, O=30%, S=60%',
      useCase: 'Perfis amplos são mais fáceis de combinar com funções específicas - indicadores claros de onde se destacam e onde podem ter dificuldades'
    },
    narrow: {
      title: 'Perfil Estreito',
      definition: 'Pontuações agrupadas em torno do meio (ex: todas 45%-55%)',
      characteristics: [
        'Mais adaptável e versátil',
        'Pode se ajustar a diferentes situações',
        'Pode faltar forças destacadas',
        'Preferências comportamentais menos previsíveis',
        'Pode se adequar a várias funções, mas pode não se destacar em posições especializadas'
      ],
      example: 'D=55%, C=48%, T=52%, O=45%, S=50%',
      useCase: 'Perfis estreitos são generalistas - adaptáveis, mas podem precisar de orientação clara sobre expectativas e foco de desenvolvimento'
    },
    interpretation: 'Nem amplo nem estreito é melhor - depende da função. Funções especializadas precisam de perfis amplos com o padrão certo; funções generalistas podem se beneficiar de perfis mais estreitos.'
  },

  roleMatching: {
    title: 'Exemplos de Correspondência de Funções',
    subtitle: 'Veja como diferentes perfis DCTOS correspondem a funções específicas',
    keyTraits: 'Traços Comportamentais Principais',
    whyItFits: 'Por Que Este Perfil Se Adequa',
    roles: {
      salesManager: {
        title: 'Gerente de Vendas',
        pattern: 'Alto D, Alto C, Baixo T',
        traits: 'Assertivo, persuasivo, socialmente confiante, rápido, competitivo',
        fit: 'Impulsiona resultados através de carisma e urgência. Pode influenciar clientes e liderar equipes com confiança. Prospera em ambientes competitivos orientados a metas.'
      },
      accountant: {
        title: 'Contador',
        pattern: 'Baixo D, Alto O, Alto S',
        traits: 'Orientado a detalhes, analítico, metódico, preciso, sistemático',
        fit: 'Garante precisão e conformidade através de análise cuidadosa. Confortável trabalhando independentemente com números. Prefere trabalho estruturado e previsível.'
      },
      customerSupport: {
        title: 'Especialista em Suporte ao Cliente',
        pattern: 'Baixo D, Alto T, Baixo S',
        traits: 'Paciente, empático, estável, focado em pessoas, evita conflitos',
        fit: 'Fornece suporte calmo e consistente. Naturalmente paciente com clientes frustrados. Valoriza relacionamentos sobre dados. Cria harmonia e confiança.'
      },
      projectManager: {
        title: 'Gerente de Projetos',
        pattern: 'Alto D, Alto O',
        traits: 'Decisivo, organizado, impulsiona responsabilidade, planeja minuciosamente',
        fit: 'Mantém projetos no caminho através de estrutura e assertividade. Pode tomar decisões difíceis mantendo planos detalhados. Equilibra pessoas e processos.'
      },
      creativeDirector: {
        title: 'Diretor Criativo',
        pattern: 'Alto C, Baixo T, Baixo O',
        traits: 'Inovador, flexível, intuitivo, energético, focado no panorama geral',
        fit: 'Gera ideias frescas e inspira equipes. Confortável com ambiguidade e mudança. Pensa criativamente em vez de analiticamente. Prospera na variedade.'
      },
      dataAnalyst: {
        title: 'Analista de Dados',
        pattern: 'Baixo C, Alto O',
        traits: 'Analítico, focado em detalhes, independente, lógico, minucioso',
        fit: 'Excelente em encontrar padrões nos dados. Prefere trabalhar sozinho com números. Toma decisões baseadas em evidências. Valoriza precisão sobre velocidade.'
      }
    },
    note: {
      title: 'Importante:',
      description: 'Estes são padrões ideais, não requisitos absolutos. Muitas pessoas bem-sucedidas adaptam seu estilo natural para se adequar à sua função. Use-os como guias, não como regras rígidas.'
    }
  },

  applications: {
    readingProfiles: {
      title: 'Como Ler um Perfil',
      steps: [
        'Observe o padrão geral - quais traços são altos/baixos?',
        'Verifique a largura - especializado ou versátil?',
        'Considere combinações de traços - como eles interagem?',
        'Compare com os requisitos da função'
      ]
    },
    combinations: {
      title: 'Combinações de Traços Comuns',
      patterns: [
        {
          traits: 'Alto D + Baixo T',
          meaning: 'Urgente/motivado - quer resultados rápidos e mudanças rápidas',
          workplace: 'Ótimo para gestão de crises, transformações, execução rápida'
        },
        {
          traits: 'Alto C + Alto D',
          meaning: 'Líder carismático - socialmente confiante e assertivo',
          workplace: 'Excelente em vendas, liderança, funções voltadas ao público'
        },
        {
          traits: 'Baixo D + Alto T',
          meaning: 'Apoiador estável - colaborativo e consistente',
          workplace: 'Ideal para atendimento ao cliente, operações, coordenação de equipe'
        },
        {
          traits: 'Alto O + Alto S',
          meaning: 'Perfeccionista analítico - orientado a detalhes e lógico',
          workplace: 'Perfeito para finanças, controle de qualidade, conformidade'
        },
        {
          traits: 'Baixo O + Baixo S',
          meaning: 'Inovador criativo - flexível e intuitivo',
          workplace: 'Prospera em áreas criativas, inovação, desenvolvimento de pessoas'
        }
      ]
    },
    personaGap: {
      title: 'Comportamento Self vs Work',
      definition: 'A diferença entre como alguém é naturalmente (Self) e como se adapta no trabalho (Work)',
      interpretation: 'Lacunas grandes sugerem adaptação comportamental ou mascaramento - isso pode ser energizante para alguns, exaustivo para outros',
      redFlags: 'Lacunas muito grandes (>30 pontos percentuais) em vários traços podem indicar inadequação de função ou risco de esgotamento'
    }
  },

  reasoning: {
    title: 'Pensamento Dedutivo vs Indutivo',
    subtitle: 'Compreendendo como as pessoas abordam problemas e tomam decisões',
    characteristics: 'Características',
    example: 'Exemplo no Trabalho',
    bestFor: 'Mais Adequado Para',
    deductive: {
      title: 'Dedutivo (D > C)',
      subtitle: 'D maior que C',
      definition: 'Começa com teorias gerais, regras ou princípios e os aplica a situações específicas. Move-se do panorama geral para os detalhes.',
      characteristics: [
        'Baseado em regras e estrutura primeiro',
        'Teórico e orientado por hipóteses',
        'Aplica conhecimento existente a novas situações',
        'Prefere estrutura e métodos estabelecidos'
      ],
      example: 'Se nossa política determina X, então nesta situação devemos fazer Y',
      bestFor: 'Implementação de políticas, trabalho de conformidade, processos padronizados, aplicação de estruturas comprovadas'
    },
    inductive: {
      title: 'Indutivo (C > D)',
      subtitle: 'C maior que D',
      definition: 'Começa com observações específicas, dados ou casos e constrói conclusões gerais. Move-se dos detalhes para o panorama geral.',
      characteristics: [
        'Orientado por dados e evidências primeiro',
        'Empírico e busca padrões',
        'Constrói teorias a partir de observações',
        'Prefere exploração e descoberta'
      ],
      example: 'Observamos X acontecendo repetidamente em vários casos, então podemos concluir Y',
      bestFor: 'Pesquisa, resolução de problemas, inovação, desenvolvimento de estratégia, encontrar novos padrões'
    },
    insight: {
      title: 'Insight Chave',
      description: 'Nenhum estilo de raciocínio é inerentemente melhor. Solucionadores de problemas eficazes usam ambos dependendo do contexto. Quando você tem estruturas comprovadas, o pensamento dedutivo garante consistência. Ao enfrentar problemas novos ou buscar inovação, o pensamento indutivo revela novos padrões.',
      connectionTitle: 'Conexão com Perfis Comportamentais',
      connection: 'Indivíduos com alto Style (S) frequentemente tendem naturalmente ao raciocínio dedutivo—aplicando estruturas lógicas e regras. Indivíduos com baixo Style (S) tendem ao raciocínio indutivo—lendo padrões no comportamento humano e contexto. No entanto, esta é uma tendência, não uma limitação, e as pessoas podem desenvolver ambas as abordagens.'
    }
  },

  executionSpeed: {
    title: 'O Gap D-T: Velocidade de Execução',
    subtitle: 'A relação entre Drive (D) e Task (T) determina o ritmo preferido de trabalho e tomada de decisão',
    gapLabel: 'Diferença',
    fast: {
      title: 'D > T (Execução Rápida)',
      subtitle: 'Maior Urgência',
      definition: 'Quando Drive excede Task, a pessoa prefere ação rápida e decisiva. Quanto maior a diferença, mais urgente é o estilo de execução.',
      characteristics: [
        'Toma decisões rápidas',
        'Impaciente com atrasos ou processos lentos',
        'Orientado para a ação, quer resultados agora',
        'Impulsiona mudanças e transformações rápidas',
        'Pode parecer apressado ou insistente para os outros'
      ],
      example: '"Vamos decidir agora e resolver problemas conforme surgirem"',
      bestFor: 'Gestão de crises, fechamento de vendas, turnarounds, ambientes de startups',
      watchouts: 'Pode comprometer a qualidade, negligenciar detalhes importantes, criar estresse para colegas de ritmo mais lento'
    },
    deliberate: {
      title: 'T > D (Ritmo Deliberado)',
      subtitle: 'Estável & Metódico',
      definition: 'Quando Task excede Drive, a pessoa prefere uma abordagem medida e minuciosa. Quanto maior a diferença, mais metódico é o estilo.',
      characteristics: [
        'Leva tempo para considerar opções',
        'Valoriza minuciosidade acima de velocidade',
        'Mantém produção constante e consistente',
        'Prefere processos comprovados',
        'Pode parecer lento para colegas urgentes'
      ],
      example: '"Vamos pensar bem nisso antes de nos comprometer"',
      bestFor: 'Garantia de qualidade, operações, suporte ao cliente, conformidade, pesquisa',
      watchouts: 'Pode perder prazos urgentes, lento para responder em crises, pode frustrar colegas de ritmo rápido'
    },
    balanced: {
      title: 'D ≈ T (Ritmo Equilibrado)',
      description: 'Quando Drive e Task são similares, a pessoa pode adaptar seu ritmo à situação - urgente quando necessário, paciente quando apropriado.'
    },
    insight: {
      title: 'Insight Chave',
      description: 'Nem execução rápida nem lenta é inerentemente melhor. O ritmo certo depende do papel e contexto. Corresponda expectativas de velocidade de execução aos requisitos do cargo.',
      howToUse: 'Como aplicar isso:',
      howToUseDescription: 'Cargos com alto gap D-T (vendas, gestão de crises) precisam de perfis D > T. Cargos de baixa urgência (suporte, QA) beneficiam-se de perfis T > D. Cargos equilibrados funcionam com ambos.'
    }
  },

  talentDesign: {
    title: 'Design de Talentos: Construindo o Time Certo',
    subtitle: 'Como montar equipes de alto desempenho baseadas na fase da empresa',
    introduction: 'Todo time precisa de três forças trabalhando em harmonia: Gás para impulsionar crescimento, Cola para unir pessoas, e Freio para garantir excelência. O equilíbrio certo depende da fase da sua empresa.',
    forcesTitle: 'As Três Forças',
    forces: {
      gas: {
        title: '⛽ GÁS',
        trait: 'D (Drive)',
        description: 'Impulsiona crescimento e dirige o movimento para frente. Gás ultrapassa limites, assume riscos calculados e acelera o progresso.',
        characteristics: [
          'Impulsiona resultados e pressiona por ação',
          'Toma iniciativa sem esperar permissão',
          'Desafia o status quo e rompe barreiras',
          'Confortável com incerteza e mudança rápida'
        ]
      },
      glue: {
        title: '🧲 COLA',
        trait: 'C (Connection)',
        description: 'Une pessoas e constrói cultura. Cola cria coesão, facilita comunicação e mantém o espírito de equipe.',
        characteristics: [
          'Constrói relacionamentos entre times e departamentos',
          'Facilita comunicação e resolve conflitos',
          'Cria senso de pertencimento e propósito compartilhado',
          'Energiza outros e mantém o moral'
        ]
      },
      brake: {
        title: '🛑 FREIO',
        trait: 'O (Order)',
        description: 'Garante excelência e mantém qualidade. Freio previne erros custosos, reforça padrões e protege o que funciona.',
        characteristics: [
          'Captura erros antes que se tornem problemas',
          'Mantém sistemas, processos e documentação',
          'Garante conformidade e padrões de qualidade',
          'Fornece estabilidade e previsibilidade'
        ]
      }
    },
    phasesTitle: 'Composição de Time Baseada em Fase',
    phases: {
      startup: {
        title: '🚀 Fase Startup',
        primaryNeed: 'Velocidade & Crescimento',
        focus: 'Muito GÁS (Alto D)',
        whatToLookFor: 'Contrate impulsionadores que ultrapassam limites, toleram ambiguidade, se movem rápido e não têm medo de quebrar coisas. Muito freio vai desacelerar você antes de encontrar product-market fit.'
      },
      scaling: {
        title: '📈 Fase de Crescimento/Escala',
        primaryNeed: 'Cultura & Alinhamento',
        focus: 'Adicione COLA (Alto C)',
        whatToLookFor: 'Traga conectores que constroem pontes entre times crescentes, mantêm a cultura enquanto escala, e mantêm todos alinhados na missão. Sem cola, times se fragmentam e silos se formam.'
      },
      mature: {
        title: '🏢 Fase Madura/Otimização',
        primaryNeed: 'Excelência & Eficiência',
        focus: 'Fortaleça o FREIO (Alto O)',
        whatToLookFor: 'Recrute guardiões de qualidade que reforçam padrões, previnem erros custosos em sistemas estabelecidos, e otimizam o que já funciona. Freio protege seus ganhos e garante excelência sustentável.'
      }
    },
    warningsTitle: 'Avisos de Equilíbrio',
    warnings: [
      {
        condition: 'Todo GÁS, sem FREIO',
        consequence: 'Caos, burnout, problemas de qualidade, erros custosos que poderiam ter sido prevenidos'
      },
      {
        condition: 'Toda COLA, sem GÁS',
        consequence: 'Muitas reuniões e conversa, mas sem execução ou movimento para frente'
      },
      {
        condition: 'Todo FREIO, sem GÁS',
        consequence: 'Paralisia por análise, oportunidades perdidas, morte lenta por super-otimização'
      }
    ],
    practicalApplication: {
      title: '💡 Conclusão Chave',
      content: 'Não existe composição de time "melhor" universal. Uma startup precisa de combustível diferente de uma empresa Fortune 500. Audite seu time atual: Qual força está faltando? Qual força você tem em excesso para sua fase atual? Então contrate de acordo.'
    }
  }
};
